import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { SelectItem } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  IAmendIncidentDueDateDTO,
  IIncidentCloseDTO,
  IIncidentDto,
} from '../../interfaces/incident';
import { IIncidentFilter } from '../../interfaces/incidents-page-filter';
import { IPageResult } from '../../interfaces/page-result';
import { IncidentPageTabState } from '../../services';
import { IncidentsPageStateService } from '../../services';
import { IncidentsService } from '../../services';
import { LoggerService } from '../../services';
import { TaggedLogger } from '../../services';
import { UserService } from '../../services';
import { flatten } from '../../utils/flatten';
import moment from 'moment';
import { startOfDay } from 'date-fns';
import { Message } from '../../classes/Message';
import { MessageService } from 'primeng';

@Component({
  selector: 'job-incidents-grid',
  templateUrl: 'incidents-grid.component.html',
  styleUrls: ['./incidents-grid.component.scss'],
})
export class IncidentsGridComponent implements OnInit {
  private logger: TaggedLogger;

  state: IncidentPageTabState;

  // Dialogs
  raiseIncidentDialog: boolean = false;
  assignIncidentDialog: boolean = false;
  closeIncidentDialog: boolean = false;
  amendDueDateDialog: boolean = false;

  //Forms
  raiseIncidentForm: UntypedFormGroup;
  assignIncidentForm: UntypedFormGroup;
  closeIncidentForm: UntypedFormGroup;
  amendDueDateForm: UntypedFormGroup;

  @Input() set opened(value: boolean) {
    this._opened = value;
    this.state = value ? this.stateService.openedTabState : this.stateService.closedTabState;
  }

  get opened(): boolean {
    return this._opened;
  }

  private _opened: boolean;

  // incident item selected for closure or amending
  private selectedItem: IIncidentDto;
  closureReasons: SelectItem[] = [];

  public currentUserId: string;
  public selectedIncidents: IIncidentDto[];

  @Output() onRefreshAllRequest: EventEmitter<any> = new EventEmitter<any>();

  loading: boolean;
  page: IPageResult<IIncidentDto> = { items: [], total: 0 };
  private _loadingSubject: Subject<IIncidentFilter>;

  assignees: SelectItem[] = [];
  customers: SelectItem[] = [];
  suppliers: SelectItem[] = [];
  incidentTypes: SelectItem[] = [];
  selectedClosureReasonId: any;

  currentDate = startOfDay(new Date());

  constructor(
    private incidentsService: IncidentsService,
    public stateService: IncidentsPageStateService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private route: ActivatedRoute,
    private messageService: MessageService,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.taggedLogger('IncidentsGridComponent');
    this._loadingSubject = new Subject<IIncidentFilter>();
    this._loadingSubject
      .asObservable()
      .pipe(debounceTime(500))
      .subscribe((f) => {
        this.loading = true;
        this.incidentsService.getPage(f).then((res) => {
          this.page = res;
          this.loading = false;
        });
      });

    this.raiseIncidentForm = formBuilder.group({
      comment: ['', [Validators.required, Validators.maxLength(1000)]],
      assigneeId: ['', Validators.required],
    });

    this.assignIncidentForm = formBuilder.group({
      assigneeId: ['', Validators.required],
    });

    this.closeIncidentForm = formBuilder.group({
      closureReasonId: ['', Validators.required],
      closureNotes: ['', [Validators.maxLength(1024)]],
    });

    this.amendDueDateForm = formBuilder.group({
      dueBy: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    if (this._opened) {
      this.route.params.subscribe((params: Params) => {
        let isMine = params['mine'];
        let setCheckbox: boolean = false;

        if (isMine != undefined) {
          // switch on the 'Assigned To Me' checkbox
          setCheckbox = true;
        }

        this.state.filter.filters.assignedToMe.value = setCheckbox;
      });

      this.incidentsService.getCustomers().then((result) => {
        this.customers = result;
      });

      this.incidentsService.getSuppliers().then((result) => {
        this.suppliers = result;
      });
    }

    this.incidentTypes.push(<SelectItem>{ value: -1, label: 'Any' });
    this.incidentsService.getIncidentTypes().then((result) => {
      result.forEach((x) => {
        this.incidentTypes.push(<SelectItem>{ value: x.id, label: x.name });
      });
    });

    this.incidentsService.getAssignees().then((res) => {
      this.assignees = res.map((x) => <SelectItem>{ value: x.value.id, label: x.label });
    });

    this.incidentsService.getClosureReasonsForDropdown().then((res) => {
      this.closureReasons = res;

      if (this.closureReasons && this.closureReasons.length > 0) {
        this.selectedClosureReasonId = this.closureReasons[0].value;
      }
    });

    // todo: getting NRE here, check it's expected then fix
    try {
      this.currentUserId = this.userService.currentUser.id;
    } catch (error) {
      this.logger.error('Error:', error);
    }

    this.search();
  }

  onGridLazyLoad(event: any) {
    this.state.filter.first = event.first;
    this.state.filter.rows = event.rows;
    this.state.filter.sortField = event.sortField;
    this.state.filter.sortOrder = event.sortOrder;
    this.search();
  }

  search() {
    this._loadingSubject.next(this.state.filter);
  }

  clearFilter() {
    this.state.init();
    this.search();
  }

  @ViewChild('assigneeSelectionPanel') assigneeSelectionPanel: OverlayPanel;
  private _currentIncidentForAssignment: IIncidentDto;

  openAssigneeSelectionPanel(event: any, item: IIncidentDto) {
    console.log('ITEM ', item);
    this._currentIncidentForAssignment = item;
    this.assigneeSelectionPanel.toggle(event);
  }

  confirmAssigneeSelection() {
    this.assignToMe(this._currentIncidentForAssignment);
    this.assigneeSelectionPanel.hide();
  }

  otherAssigneeSelection() {
    this.assignIncidentForm.reset();
    this.assignIncidentDialog = true;
    this.assigneeSelectionPanel.hide();
  }

  confirmOtherAssigneeSelection() {
    const data = {
      incidentId: this._currentIncidentForAssignment.id,
      ...this.assignIncidentForm.value,
    };
    this.incidentsService.assigneeToUser(data.incidentId, data.assigneeId).then(() => {
      this.assignIncidentDialog = false;
      this.search();
    });
  }

  assignToMe(item: IIncidentDto) {
    this.incidentsService.assigneeToMe(item.id).then(() => this.search());
  }

  closeIncident(item: IIncidentDto) {
    // show confirmation/closure reason dialog
    this.selectedItem = item;
    this.closeIncidentDialog = true;
  }

  confirmCloseIncident() {
    if (this.selectedItem != null) {
      let closeDto = this.setCloseDTOValues(this.closeIncidentForm.value, this.selectedItem);

      //console.log("close DTO: ", closeDto);

      this.incidentsService.close(closeDto).then((res) => {
        this.selectedItem.closed = res;
        this.clearClosureVars();
        this.search();
        this.onRefreshAllRequest.emit();
      });
    } else if (this.selectedIncidents != null && this.selectedIncidents.length > 0) {
      let closeDtos: IIncidentCloseDTO[] = [];
      let baseClose = this.closeIncidentForm.value;

      this.selectedIncidents.forEach((x) => {
        let closeDto = this.setCloseDTOValues(baseClose, x);
        closeDtos.push(closeDto);
      });

      this.incidentsService.groupClose(closeDtos).then((res) => {
        this.clearClosureVars();
        this.search();
        this.onRefreshAllRequest.emit();
      });
    }
  }

  groupCloseIncidents() {
    this.closeIncidentDialog = true;
    this.selectedItem = null;
  }

  setCloseDTOValues(baseDTO: IIncidentCloseDTO, item: IIncidentDto): IIncidentCloseDTO {
    let closeDto = Object.assign({}, baseDTO);

    closeDto.id = item.id;
    closeDto.typeId = item.typeId;
    closeDto.typeName = item.typeName;
    closeDto.jobId = item.jobId;
    closeDto.taskId = item.taskId;
    closeDto.jobNumber = item.jobNumber;
    closeDto.vehicleReg = item.vehicleReg;

    return closeDto;
  }

  clearClosureVars() {
    this.selectedItem = null;
    this.closeIncidentForm.reset();
    this.closeIncidentDialog = false;
  }

  getDateDescription(date: Date): string {
    return date ? moment.duration(moment(date).diff(moment())).humanize(true) : 'N/A';
  }

  getDateLabel(date: Date, hideTime: boolean = false): string {
    return date ? moment(date).format(hideTime ? "DD MMM 'YY" : "DD MMM 'YY HH:mm") : '';
  }

  getPickupDescription(item: IIncidentDto): string {
    if (!item.pickupLatestDateTime) return null;
    let latest = item.pickupLatestDateTime
      ? moment(item.pickupLatestDateTime).format("ddd DD MMM 'YY HH:mm")
      : 'N/A';

    return latest;
  }

  getDropoffDescription(item: IIncidentDto): string {
    if (!item.dropoffLatestDateTime) return null;
    let latest = item.dropoffLatestDateTime
      ? moment(item.dropoffLatestDateTime).format("ddd DD MMM 'YY HH:mm")
      : 'N/A';

    return latest;
  }

  getRowStyle(rowData: any, index: number) {
    return 'no-cell-wrap ' + (!rowData.assigneeId ? 'grid-row-bold' : '');
  }

  // Raise custom dialog
  showRaiseIncidentDialog() {
    this.raiseIncidentForm.reset();
    this.raiseIncidentDialog = true;
  }

  raiseIncident() {
    this.incidentsService.raiseManualIncident(this.raiseIncidentForm.value).then((res) => {
      this.raiseIncidentDialog = false;
      this.search();
    });
  }

  downloadToCSV() {
    this.incidentsService.downloadToCSV(
      flatten(this.state.filter),
      this.state.filter.filters.opened,
    );
  }

  isOverdue(dueDate: Date): boolean {
    if (dueDate) {
      return new Date(dueDate) <= this.currentDate;
    }

    return false;
  }

  showAmendDueDateDialog(item: IIncidentDto) {
    this.selectedItem = item;
    this.amendDueDateDialog = true;
    this.amendDueDateForm.controls.dueBy.setValue(
      item.dueBy == null ? this.currentDate : new Date(item.dueBy),
    );
  }

  amendDueDate() {
    const dto = <IAmendIncidentDueDateDTO>{
      id: this.selectedItem.id,
      dueBy: this.amendDueDateForm.controls.dueBy.value,
    };

    this.incidentsService
      .amendDueDate(dto)
      .then(() => {
        this.search();
      })
      .catch(() => {
        this.messageService.add(
          Message.Error(
            'An error occurred while attempting to amend the due date of the incident. ' +
              'Please try again.',
            'Amend Due Date',
          ),
        );
      });

    this.selectedItem = null;
    this.amendDueDateDialog = false;
  }
}
